import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { BookingSaveKind, CreateUpdateBooking, ResourceFiltersTypes } from "../../createOrderTypes"
import { WeeklyResourceBookingCalendar } from "./resourceBookingCalendars/WeeklyResourceBookingCalendar"
import { TimelineCalendarEvent, TimelineCalendarResource } from "./calendar/fullCalendarTypes"
import { ResourceBookingCalendar } from "./resourceBookingCalendars/ResourceBookingCalendar"
import { BookingViewMenu, ViewType } from "./resourceBookingCalendars/BookingViewMenu"
import { ResourceFilters } from "./resourceFilters/ResourceFilters"
import {
    saveServiceCenterFilter,
    SERVICE_CENTER_FILTER_IDENTIFIER,
} from "./resourceFilters/timelineLocationFilterStorage"
import { countHiddenByMeetingFit, createInitialFilters } from "./resourceFilters/meetingFit"
import { useMeetingAvailability } from "./resourceFilters/useMeetingAvailability"
import { HiddenResourcesNotice } from "./resourceFilters/HiddenResourcesNotice"
import { ResourceBookingControls } from "./ResourceBookingControls"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { filterResources } from "./helpers"
import {
    ConfigQuery,
    CreateUpdateBookingResponse,
    fetchTimelineResources,
    TimelineResourcesResponse,
} from "../../createOrderApi"
import { OrderFormStatus } from "../../mainForm/helpers"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { ResourceBookingConfirmDialog } from "./ResourceBookingConfirmDialog"
import { useResourceBookingDraft } from "./ResourceBookingDraftContext"
import { useConfirmBookingDialog } from "./ResourceBookingConfirmDialogContext"
import { TimelineContextProvider } from "./context/TimelineContextProvider"
import { ResourceDetailsProvider } from "./context/ResourceDetailsProvider"
import { ResourceLabelSharedProvider } from "./context/ResourceLabelContext"
import { useBookingAvailabilityGuard } from "./useBookingAvailabilityGuard"
import { useCreateOrderForm, useCreateOrderFormSetOrder } from "../../mainForm/CreateOrderFormContext"
import { ResourceBookingCalendarSkeleton } from "./ResourceBookingCalendarSkeleton"

const useStyles = makeStyles()((theme) => ({
    calendarWrapper: {
        position: "relative",
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
    },
    headerWrap: {
        padding: ".75rem 1rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
    },
    button: {
        color: theme.palette.tailwind.slate[800],
        padding: ".5rem 1rem",
        flex: "none",
    },
}))

const transformData = (data: TimelineResourcesResponse) => {
    const serviceCentersData = data?.serviceCenters

    if (!serviceCentersData) {
        return { calendarResources: [], groupImagesMap: new Map<string, string>() }
    }

    const groupImagesMap = new Map<string, string>()
    const calendarResources: TimelineCalendarResource[] = []

    serviceCentersData.forEach((group) => {
        if (group.floorPlan?.imageUrl) {
            groupImagesMap.set(group.name, group.floorPlan.imageUrl)
        }

        group.resources.forEach((resource) => {
            calendarResources.push({
                id: String(resource.id),
                uid: resource.uid,
                location: group.name,
                locationId: `${String(group.id)}_${String(resource.id)}`,
                serviceCenterUid: group.uid,
                title: resource.displayName || resource.name,
                seats: resource.room?.seats,
                floor: resource.room?.floorDetails?.value,
                avEquipments: resource.room?.avEquipments || [],
                buffer: resource.buffer,
                tableSetups: resource.room?.tableSetups,
                outOfService: resource.outOfService,
                image: resource.image,
                meetingUsage: resource.room?.meetingUsage,
                // hasMessagingEnabled: !!group.messaging,
                type: resource.type,
                accessControl: resource.accessControl,
            })
        })
    })

    return { calendarResources, groupImagesMap }
}

type ResourceBookingProps = Readonly<{
    handleOpenSuccessDialog: (
        orderDetails: CreateUpdateBookingResponse,
        order: CreateUpdateBooking,
        save: BookingSaveKind,
    ) => void
    handleClearResourceIds: () => void
    orderDetails?: MeetingServiceOrder
    orderFormStatusRef: React.MutableRefObject<OrderFormStatus>
}>

export function ResourceBooking({
    handleOpenSuccessDialog,
    handleClearResourceIds,
    orderDetails,
    orderFormStatusRef,
}: ResourceBookingProps) {
    const { user, orderContext, setOrderContext, flowConfig } = useCreateOrderForm()
    const { switchToOrder } = useCreateOrderFormSetOrder()
    const { confirmBookingDialog, setConfirmBookingDialog } = useConfirmBookingDialog()
    const { orderId, mode } = orderContext
    const isOutlookFlow = !!flowConfig?.flow

    // Recurring bookings (an Outlook series) behave like "Reserve period": the user selects
    // resources via checkboxes instead of drawing on the timeline. The period is dictated by the
    // series, so this mode is forced on and cannot be switched off.
    const isRecurringBooking = Boolean(flowConfig?.order?.meetingDetails?.recurrence?.pattern)

    const { classes } = useStyles()
    const {
        draftEvent: event,
        setDraftEvent,
        syncDraftToFormik,
        initialEventStateId,
        handleCancelClick,
    } = useResourceBookingDraft()

    // `outlookAttendeeCount` is only sent by newer add-ins; `deliveryDetails.sittings` carries the same
    // headcount from older ones and is a safe fallback for a filter value (unlike the attendees opt-in,
    // which needs the newer handshake and must not fall back).
    const outlookGuestCount =
        flowConfig?.order?.meetingDetails?.outlookAttendeeCount ?? flowConfig?.order?.deliveryDetails?.sittings ?? 0

    const [selectedView, setSelectedView] = useState(ViewType.TIMELINE)
    const [filters, setFilters] = useState<ResourceFiltersTypes>(() =>
        createInitialFilters({ userId: user.userId, isOutlookFlow, guestCount: outlookGuestCount, isRecurringBooking }),
    )
    // Bumped only when the location filter is reset programmatically, to remount the chips with it.
    const [chipsSeed, setChipsSeed] = useState(0)
    const hasReconciledLocationsRef = useRef(false)

    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    const {
        data: { calendarResources, groupImagesMap } = {
            calendarResources: [],
            groupImagesMap: new Map<string, string>(),
        },
        isLoading,
    } = useApiQuery(fetchTimelineResources, {
        queryName: ConfigQuery.TIMELINE_RESOURCES_FOR_USER,
        dependencies: {
            userId: user.userId,
            resourceTypes: filters.filters
                .find((f) => f.identifier === "resourceTypeIds")
                ?.values.map((v) => String(v.key).trim().split(" ")[0].toUpperCase())
                .join(","),
        },

        select: transformData,
    })

    const activeResourceIdsKey = selectedView === ViewType.TIMELINE ? (event?.resourceIds?.join("|") ?? "") : ""
    const activeResourceIdsForFiltering = useMemo<readonly string[] | null>(
        () => (activeResourceIdsKey ? activeResourceIdsKey.split("|") : null),
        [activeResourceIdsKey],
    )

    const { meetingWindow, busyResourceIds, isAwaitingAvailability } = useMeetingAvailability({
        userId: user.userId,
        calendarResources,
        filters,
        selectedView,
        draftEvent: event,
    })

    const filteredResources = useMemo(
        () =>
            filterResources(
                calendarResources,
                filters.filters,
                searchRegex,
                activeResourceIdsForFiltering,
                busyResourceIds,
            ),
        [calendarResources, filters.filters, searchRegex, activeResourceIdsForFiltering, busyResourceIds],
    )

    const hiddenCount = useMemo(() => {
        if (!isOutlookFlow) return 0

        return countHiddenByMeetingFit({
            calendarResources,
            filters: filters.filters,
            searchRegex,
            activeResourceIds: activeResourceIdsForFiltering,
            visibleCount: filteredResources.length,
        })
    }, [
        isOutlookFlow,
        calendarResources,
        filters.filters,
        searchRegex,
        activeResourceIdsForFiltering,
        filteredResources,
    ])

    const handleShowAll = useCallback(() => {
        setFilters((prev) => ({
            ...createInitialFilters({ userId: user.userId, isOutlookFlow: false, guestCount: 0 }),
            // Neither belongs to the meeting narrowing; `isBookedPeriod` would also desync from the period
            // `BookPeriodFilter` keeps in its own state and still shows on its chip.
            search: prev.search,
            isBookedPeriod: prev.isBookedPeriod,
        }))
        setChipsSeed((seed) => seed + 1)
    }, [user.userId])

    // Only the locations on screen: resources of the active booking survive `filterResources`, so
    // their events (and the overlap index built from them) are still fetched.
    const serviceCenterIdsForEventsKey = useMemo(
        () => [...new Set(filteredResources.map((r) => r.locationId.split("_")[0]))].sort().join("|"),
        [filteredResources],
    )
    // Keyed by the id set, so picking a resource or typing in search does not recreate the event sources.
    const allServiceCenterIdsForEvents = useMemo(
        () => (serviceCenterIdsForEventsKey ? serviceCenterIdsForEventsKey.split("|") : []),
        [serviceCenterIdsForEventsKey],
    )

    useEffect(() => {
        const values = filters.filters.find((f) => f.identifier === SERVICE_CENTER_FILTER_IDENTIFIER)?.values ?? []
        saveServiceCenterFilter(user.userId, values)
    }, [filters.filters, user.userId])

    // A restored location that no longer exists would leave the timeline empty with no way back
    // other than the chip, so drop the whole filter once the resources prove none of it is valid.
    useEffect(() => {
        if (hasReconciledLocationsRef.current || !calendarResources.length) return
        hasReconciledLocationsRef.current = true

        const savedValues = filters.filters.find((f) => f.identifier === SERVICE_CENTER_FILTER_IDENTIFIER)?.values
        if (!savedValues?.length) return

        const availableIds = new Set(calendarResources.map((r) => r.locationId.split("_")[0]))
        if (savedValues.some((value) => availableIds.has(String(value.value)))) return

        setFilters((prev) => ({
            ...prev,
            filters: prev.filters.filter((f) => f.identifier !== SERVICE_CENTER_FILTER_IDENTIFIER),
        }))
        setChipsSeed((seed) => seed + 1)
    }, [calendarResources, filters.filters])

    const { ensureDraftStillAvailable, isChecking } = useBookingAvailabilityGuard({ resources: calendarResources })

    const handleBookClick = useCallback(async () => {
        if (!(await ensureDraftStillAvailable(event))) return

        syncDraftToFormik()
        setConfirmBookingDialog(true)
    }, [ensureDraftStillAvailable, event, syncDraftToFormik, setConfirmBookingDialog])

    const handleOpenBookDialog = useCallback(
        async (draft: TimelineCalendarEvent) => {
            if (!(await ensureDraftStillAvailable(draft))) return

            setConfirmBookingDialog(true)
        },
        [ensureDraftStillAvailable, setConfirmBookingDialog],
    )

    const handleChangeBookingView = (view: ViewType) => {
        if (selectedView === ViewType.AVAILABLE_TIME && event) {
            setDraftEvent({ ...event, timeRange: undefined })
        }
        setSelectedView(view)
        handleCancelClick()
    }

    const handleChangeOrderContext = useCallback((id: string) => switchToOrder(+id), [switchToOrder])

    return (
        <Box className={classes.calendarWrapper}>
            <Grid
                container
                justifyContent="space-between"
                flexWrap="nowrap"
                alignItems="center"
                className={classes.headerWrap}
            >
                <ResourceFilters
                    filters={filters}
                    userId={user.userId}
                    setFilters={setFilters}
                    selectedView={selectedView}
                    chipsSeed={chipsSeed}
                    isTimeline
                    isSpecialFlow={isOutlookFlow}
                    isRecurringBooking={isRecurringBooking}
                    hasMeetingWindow={!!meetingWindow}
                />

                <BookingViewMenu selectedView={selectedView} onChangeView={handleChangeBookingView} />
            </Grid>

            {selectedView === ViewType.TIMELINE && !isAwaitingAvailability && hiddenCount > 0 && (
                <HiddenResourcesNotice count={hiddenCount} onShowAll={handleShowAll} />
            )}

            {!isLoading && !isAwaitingAvailability ? (
                <ResourceDetailsProvider>
                    {selectedView === ViewType.TIMELINE && (
                        <TimelineContextProvider
                            timelineResources={filteredResources}
                            allServiceCenterIdsForEvents={allServiceCenterIdsForEvents}
                            userId={user.userId}
                            localEventUpdateId={initialEventStateId || orderId}
                            isBookPeriodMode={!!filters.isBookedPeriod}
                            isOutlookFlow={isOutlookFlow}
                            searchRegex={searchRegex}
                        >
                            <ResourceLabelSharedProvider>
                                <ResourceBookingCalendar
                                    groupImagesMap={groupImagesMap}
                                    handleChangeOrderContext={handleChangeOrderContext}
                                />
                            </ResourceLabelSharedProvider>
                        </TimelineContextProvider>
                    )}
                    {selectedView === ViewType.AVAILABLE_TIME && (
                        <WeeklyResourceBookingCalendar
                            timelineResources={filteredResources}
                            searchRegex={searchRegex}
                            groupImagesMap={groupImagesMap}
                            handleBookClick={handleOpenBookDialog}
                        />
                    )}
                </ResourceDetailsProvider>
            ) : (
                <ResourceBookingCalendarSkeleton viewType={selectedView} />
            )}
            {selectedView === ViewType.TIMELINE && (
                <ResourceBookingControls
                    onBook={handleBookClick}
                    isLoadingButton={confirmBookingDialog || isChecking}
                    flow={flowConfig?.flow}
                    orderFormStatusRef={orderFormStatusRef}
                />
            )}

            {confirmBookingDialog && event && (
                <ResourceBookingConfirmDialog
                    open={confirmBookingDialog}
                    user={user}
                    orderId={orderId}
                    initialEventStateId={initialEventStateId}
                    orderFormStatusRef={orderFormStatusRef}
                    orderDetails={orderDetails}
                    mode={mode}
                    setOrderContext={setOrderContext}
                    handleOpenSuccessDialog={handleOpenSuccessDialog}
                    handleClearResourceIds={handleClearResourceIds}
                />
            )}
        </Box>
    )
}
